import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Chip, Tooltip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useSnackbar } from "notistack";
import { useMultiTableData } from "../../../../components/tables/hooks/useMultiTableData.jsx";
import { userOptions } from "../../../users/components/AdminUserFormDefinitions.js";
import CustomerUserActionsModal from "../../../../components/modals/CustomerUserActionsModal.jsx";

const roleLabel = (role) =>
  userOptions.rolle.find((option) => option.key === role)?.label || role;

const isFieldStaffRole = (user) =>
  user.role === "kd_aussendienst" || user.role === "kd_eingeschraenkt";
const isRestrictedAussendienst = (user) => user.role === "kd_eingeschraenkt";
// Nur kd_verwaltung wird tatsächlich benachrichtigt (siehe
// NotificationService::notifyAllCustomerRoleAndPermission()), bei jeder
// anderen Rolle bleibt die Permission wirkungslos.
const isCustomerAdministrationRole = (user) => user.role === "kd_verwaltung";
const receivesInternalReport = (user) =>
  !!user.permissions?.includes("claim.customer.receive_pre_check_customer");

const BooleanIcon = ({ value }) =>
  value ? (
    <CheckCircleIcon fontSize="small" color="success" />
  ) : (
    <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
  );

/**
 * Konsolidierte Benutzer-Tabelle eines Kunden — Darstellung orientiert sich an
 * den übrigen Kunden-Tabs (DataTable/useMultiTableData). Der Endpoint
 * (KundeController::customerUsers()) unterstützt nur Pagination, kein
 * Spatie-Filtern/Sortieren - daher hier bewusst deaktiviert statt eine
 * wirkungslose Filter-/Sortier-UI vorzutäuschen. Außendienstmitarbeiter-Rolle
 * und Schadenreport-Empfänger-Berechtigung werden nur als Icon angezeigt;
 * Klick auf eine Zeile öffnet ein Modal, in dem die Aktionen erst beim
 * Speichern ausgelöst werden (siehe CustomerUserActionsModal).
 */
export default function CustomerUsersTable({ customerId }) {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedUser, setSelectedUser] = useState(null);

  const tableKey = "customerUsers";
  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Benutzer",
        route: `/data/kundes/${customerId}/users`,
        pageSize: 10,
        storageKey: "v2_table_customer_users",
      },
    ],
    [customerId]
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );
  const tableState = tableStates[tableKey] || {};
  const rows = tableState.rows || [];

  useEffect(() => {
    fetchData(tableKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const columns = useMemo(
    () => [
      { accessorKey: "full_name", header: "Name", size: 200 },
      { accessorKey: "email", header: "E-Mail", size: 220 },
      {
        accessorKey: "role",
        header: "Rolle",
        size: 200,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Chip label={roleLabel(row.original.role)} size="small" />
        ),
      },
      {
        accessorKey: "is_field_staff",
        header: "Außendienstmitarbeiter",
        size: 180,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          const user = row.original;
          if (!isFieldStaffRole(user)) {
            return (
              <Tooltip title="Nur für Außendienst-Rollen relevant">
                <span>—</span>
              </Tooltip>
            );
          }
          return <BooleanIcon value={isRestrictedAussendienst(user)} />;
        },
      },
      {
        accessorKey: "receives_internal_report",
        header: "Empfänger interner Schadenreport",
        size: 220,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          const user = row.original;
          if (!isCustomerAdministrationRole(user)) {
            return (
              <Tooltip title="Nur für die Rolle 'Verwaltung' relevant">
                <span>—</span>
              </Tooltip>
            );
          }
          return <BooleanIcon value={receivesInternalReport(user)} />;
        },
      },
    ],
    []
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={rows}
        isLoading={tableState.isLoading}
        hasLoaded={tableState.hasLoaded}
        enableColumnFilters={false}
        enableSorting={false}
        onRowClick={(row) => setSelectedUser(row)}
        enablePagination
        manualPagination
        pageIndex={tableState.pageIndex ?? 0}
        pageSize={tableState.pageSize ?? 10}
        totalRows={tableState.totalRows ?? rows.length}
        onChangePage={(nextOrUpdater) => {
          const base = {
            pageIndex: tableState.pageIndex ?? 0,
            pageSize: tableState.pageSize ?? 10,
          };
          const next =
            typeof nextOrUpdater === "function"
              ? nextOrUpdater(base)
              : nextOrUpdater;
          if (!next) return;
          updateTableState(tableKey, {
            pageIndex: next.pageIndex,
            pageSize: next.pageSize,
          });
        }}
        storageKey="v2_table_customer_users"
      />
      <CustomerUserActionsModal
        open={!!selectedUser}
        user={selectedUser}
        customerId={customerId}
        onClose={() => setSelectedUser(null)}
        onSuccess={() => fetchData(tableKey)}
      />
    </>
  );
}

CustomerUsersTable.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};
